/**
 * Fases do funil — as colunas do Kanban e o que cada uma exige para avançar.
 *
 * ── Extraído da `ConfigCrmPage` em 2026-08-03 (K-3)
 *
 * A `ConfigCrmPage` tinha **1905 linhas** e 10 abas num arquivo só. Cada aba
 * virou um item do menu da Configurações, e o painel da casca renderiza este
 * componente direto — sem passar por ela.
 *
 * A extração é passo PRÓPRIO, antes da migração, de propósito: separar "mover
 * código" de "mudar comportamento" é o que permite ler o diff de cada um.
 */
/**
 * CRM v2 — Configurações > Comercial > CRM (PRD §7).
 *
 * TODO parâmetro do Comercial mora aqui: catálogo de serviços · fases ·
 * status de lead · origens · campos obrigatórios por fase · tipos de
 * atividade · motivos de perda/descarte · documentos do Ganho · tags ·
 * construtor de automações · visibilidade por perfil · carteira (ownership).
 * (Metas têm tela própria em Comercial → Metas.)
 */

import {useState} from 'react';
import { camposComRotulo } from '../rotulo-do-campo';
import {ChevronDown, ChevronUp} from 'lucide-react';

import {Badge, Button, Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, HelpTip, Input, Switch, useToast} from '@/components/ui';
import {cn} from '@/lib/cn';
import {ApiError} from '@/lib/api';
import {mensagemErroConfig} from './erro-config';
import type {
  Fase,
} from '@/lib/crm/crm-types';
import {useCamposObrigatorios, useConfigActions, useConfigGeralActions, useFases} from '@/lib/crm/crm-store';
// O14: dropdowns de usuário do comercial listam SÓ o departamento comercial.
// O7: config completa do Orçamento (markups, régua, faixas, kits, taxas).
// A7: Modelos de orçamento nomeados (aba própria — grid de cards + drawer).
// R3: Reciclagem de leads (aba própria — leads parados voltam pro pool/redistribuição).
// Bloco C: Lembretes de atividade agendada (liga/desliga + antecedência).


export function AbaFases() {
  // CA-1: SÓ a tela de config pede as inativas (atenuadas + botão Reativar).
  // O Kanban e as demais telas seguem só com as ativas.
  const { data: fases = [] } = useFases(true);
  const acoes = useConfigActions('fases');
  const campos = useCamposObrigatorios();
  const geral = useConfigGeralActions();
  const toast = useToast();
  const [novaFase, setNovaFase] = useState('');
  const [renomeando, setRenomeando] = useState<{ id: string; nome: string } | null>(null);
  // CA-1: fluxo de desativação (confirmação → 422 fase-com-cards vira guia).
  const [desativando, setDesativando] = useState<Fase | null>(null);
  const [bloqueio, setBloqueio] = useState<{ fase: Fase; count: number } | null>(null);
  const [reordenando, setReordenando] = useState(false);
  /** Bloco 2: id da fase sendo arrastada (destaca origem e alvos validos). */
  const [arrastando, setArrastando] = useState<string | null>(null);

  const ativas = fases.filter((f) => f.ativo);
  const inativas = fases.filter((f) => !f.ativo);
  /**
   * Bloco 2: Desistência SAI da lista reordenável.
   *
   * Ela nasceu `tipo = 'aberta'` porque o CHECK de `fases.tipo` só aceita
   * aberta|ganho|perdido, e reconstruir a tabela custaria mais do que valia.
   * O efeito colateral não previsto: as setas aparecem para toda fase 'aberta',
   * então Ganho e Perdido ficaram protegidos e ela NÃO — alguém podia arrastá-la
   * pro meio do funil e mudar o significado dela sem perceber.
   *
   * Ela é fase de SAÍDA, como Ganho e Perdido. Fica ancorada antes deles.
   */
  const ehSaida = (f: Fase) => f.tipo !== 'aberta' || f.isDesistencia === true;
  const abertasAtivas = ativas.filter((f) => !ehSaida(f));
  /** Saídas em ordem canônica: Desistência antes de Ganho/Perdido. */
  const saidasAtivas = [
    ...ativas.filter((f) => f.isDesistencia === true),
    ...ativas.filter((f) => f.tipo !== 'aberta'),
  ];

  /** Aplica uma sequência nova de abertas, renumerando tudo. */
  async function persistirOrdem(novaOrdem: Fase[]) {
    const sequencia = [...novaOrdem, ...saidasAtivas];
    setReordenando(true);
    try {
      for (let k = 0; k < sequencia.length; k++) {
        if (sequencia[k].ordem !== k + 1) {
          await acoes.editar.mutateAsync({ id: sequencia[k].id, ordem: k + 1 });
        }
      }
      // A ordem nova tem que chegar ao Funil sem recarregar: `useConfigActions`
      // invalida `['v1','crm','config']`, que cobre a key das fases.
      toast.show('Ordem salva. Quem estiver com o Funil aberto vê na próxima vez que entrar nele.');
    } catch {
      toast.show('Não foi possível reordenar as fases.', { variant: 'error' });
    } finally {
      setReordenando(false);
    }
  }

  /** CA-1: setas — a alternativa de TECLADO pro arrasto. Não somem. */
  async function mover(fase: Fase, dir: -1 | 1) {
    const i = abertasAtivas.findIndex((x) => x.id === fase.id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= abertasAtivas.length || reordenando) return;
    const novaOrdem = [...abertasAtivas];
    [novaOrdem[i], novaOrdem[j]] = [novaOrdem[j], novaOrdem[i]];
    await persistirOrdem(novaOrdem);
  }

  /** Bloco 2: arrastar — o gesto natural, o mesmo do funil. */
  async function soltarEm(idArrastado: string, idAlvo: string) {
    if (idArrastado === idAlvo || reordenando) return;
    const de = abertasAtivas.findIndex((f) => f.id === idArrastado);
    const para = abertasAtivas.findIndex((f) => f.id === idAlvo);
    if (de < 0 || para < 0) return;
    const novaOrdem = [...abertasAtivas];
    const [movida] = novaOrdem.splice(de, 1);
    novaOrdem.splice(para, 0, movida);
    await persistirOrdem(novaOrdem);
  }

  async function confirmarDesativar() {
    const fase = desativando;
    if (!fase) return;
    try {
      await acoes.editar.mutateAsync({ id: fase.id, ativo: false });
      setDesativando(null);
      toast.show('Fase desativada. Ela sai do funil, mas o histórico fica.');
    } catch (err) {
      setDesativando(null);
      if (err instanceof ApiError && err.response && typeof err.response === 'object') {
        const body = err.response as { error?: string; count?: number };
        if (body.error === 'fase-com-cards') {
          setBloqueio({ fase, count: body.count ?? 0 });
          return;
        }
      }
      toast.show('Não foi possível desativar a fase.', { variant: 'error' });
    }
  }

  /**
   * 🔴 T1 (01/10/2026): o mapa saiu daqui e virou motor
   * (`../rotulo-do-campo`). ⚠️ Ele era LOCAL, e por isso o `FunilPage` não o
   * via — quando uma fase barrava o avanço, o toast dizia o nome da coluna.
   *
   * ✅ As razões de quem veio antes continuam valendo e moram no motor:
   * 'produto'/'proxima_acao' (31/08) e `data_proposta` (B5, 27/09) saíram do
   * sistema, e rótulo de campo inexistente é opção que promete o que não
   * entrega.
   */
  const CAMPO_LABEL: Record<string, string> = Object.fromEntries(
    camposComRotulo().map(({ campo, rotulo }) => [campo, rotulo]),
  );

  return (
    <div className="grid md:grid-cols-2 gap-4 items-start">
      <Card variant="flat" className="p-4 min-w-0">
        <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
          Fases do funil
          <HelpTip texto="As colunas do quadro de propostas. O card anda da esquerda pra direita até terminar em Ganho ou Perdido." />
        </h3>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
          <b>Arraste pela alça para mudar a ordem das colunas do funil</b>, ou use as setas.
          Ganho, Perdido e Desistência são fixas: ficam sempre no fim e mantêm o significado mesmo
          renomeadas.
        </p>
        <div className="space-y-1.5 mb-3">
          {ativas.map((f) => {
            const posAberta = abertasAtivas.findIndex((x) => x.id === f.id);
            return (
              <div
                key={f.id}
                // Bloco 2: arrastar e o gesto natural pra reordenar coluna de
                // kanban — e o que o vendedor faz no funil o dia todo. O
                // drag-and-drop nativo do HTML basta numa lista vertical curta;
                // trazer o dnd-kit do quadro seria peso sem retorno aqui.
                draggable={!ehSaida(f) && !reordenando}
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', f.id);
                  e.dataTransfer.effectAllowed = 'move';
                  setArrastando(f.id);
                }}
                onDragEnd={() => setArrastando(null)}
                onDragOver={(e) => {
                  if (ehSaida(f) || !arrastando || arrastando === f.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => {
                  if (ehSaida(f)) return;
                  e.preventDefault();
                  const id = e.dataTransfer.getData('text/plain');
                  setArrastando(null);
                  if (id) void soltarEm(id, f.id);
                }}
                data-testid={`cfg-fase-linha-${f.id}`}
                className={cn(
                  'rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2 transition-colors',
                  !ehSaida(f) && !reordenando && 'cursor-grab active:cursor-grabbing',
                  arrastando === f.id && 'opacity-40',
                  arrastando && arrastando !== f.id && !ehSaida(f) && 'border-red border-dashed',
                )}
              >
                {/* FASE-C: a fileira quebra em duas linhas no celular e o tipo
                    da fase virou <Badge> — nada de texto colado no nome. */}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  {/* Bloco 2: as setas eram dois chevrons cinza minusculos,
                      empilhados, sem rotulo, sem borda e sem area de clique —
                      pareciam decoracao, e o Matheus nao achou a funcao. Agora
                      tem caixa propria com contorno, e a alca de arrasto ao
                      lado. Elas NAO somem: sao a alternativa de teclado. */}
                  {!ehSaida(f) ? (
                    <span className="flex items-center gap-1 shrink-0">
                      <span
                        aria-hidden
                        title="Arraste para reordenar"
                        className="select-none px-1 text-base leading-none text-ink-3 dark:text-ink-dark-3"
                      >
                        &#10495;
                      </span>
                      <span className="flex flex-col">
                        <button
                          type="button"
                          data-testid={`cfg-fase-subir-${f.id}`}
                          aria-label={`Mover a fase ${f.nome} pra cima`}
                          disabled={posAberta <= 0 || reordenando}
                          onClick={() => void mover(f, -1)}
                          className="flex items-center justify-center w-7 h-6 rounded-t-md border border-line dark:border-line-dark border-b-0 text-ink-2 dark:text-ink-dark-2 hover:bg-paper-surface hover:text-red dark:hover:bg-paper-dark-surface disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-2"
                        >
                          <ChevronUp className="w-4 h-4" aria-hidden />
                        </button>
                        <button
                          type="button"
                          data-testid={`cfg-fase-descer-${f.id}`}
                          aria-label={`Mover a fase ${f.nome} pra baixo`}
                          disabled={posAberta === abertasAtivas.length - 1 || reordenando}
                          onClick={() => void mover(f, 1)}
                          className="flex items-center justify-center w-7 h-6 rounded-b-md border border-line dark:border-line-dark text-ink-2 dark:text-ink-dark-2 hover:bg-paper-surface hover:text-red dark:hover:bg-paper-dark-surface disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-2"
                        >
                          <ChevronDown className="w-4 h-4" aria-hidden />
                        </button>
                      </span>
                    </span>
                  ) : (
                    <span className="w-[46px] shrink-0" aria-hidden />
                  )}
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: f.cor }} />
                  <span className="flex-1 min-w-0 break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                    {f.nome}
                  </span>
                  {f.tipo !== 'aberta' && (
                    <Badge
                      variant={f.tipo === 'ganho' ? 'success' : 'danger'}
                      size="sm"
                      className="shrink-0"
                    >
                      {f.tipo === 'ganho' ? 'Ganho' : 'Perdido'}
                    </Badge>
                  )}
                  <span className="flex flex-wrap items-center gap-x-3 gap-y-1 shrink-0">
                    <button
                      type="button"
                      className="text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                      onClick={() => setRenomeando({ id: f.id, nome: f.nome })}
                    >
                      renomear
                    </button>
                    {f.tipo === 'aberta' && (
                      <button
                        type="button"
                        data-testid={`cfg-fase-desativar-${f.id}`}
                        className="text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                        onClick={() => setDesativando(f)}
                      >
                        desativar
                      </button>
                    )}
                  </span>
                </div>
                {/* E1 (mig 379) + CA-2: flags do pipeline do orçamento por fase,
                    com rótulos e "?" que separam PRODUZIR orçamento de REVISAR
                    uma versão (revisão não re-exige orçamento). */}
                {f.tipo === 'aberta' && (
                  <div className="flex flex-col gap-2 mt-2 pl-[18px]">
                    <span className="inline-flex items-center gap-1.5">
                      <Switch
                        size="sm"
                        label="Pedir data de entrega ao entrar na fase"
                        checked={Boolean(f.exigeDataEntrega)}
                        onChange={(e) =>
                          acoes.editar.mutate(
                            { id: f.id, exigeDataEntrega: e.target.checked },
                            { onError: () => toast.show('Não foi possível salvar a fase.', { variant: 'error' }) },
                          )
                        }
                      />
                      <HelpTip texto="Ao mover uma proposta pra esta fase, o sistema pede dia e hora de entrega e cobra esse prazo em dias úteis. Vale pra fase em que o orçamento é pedido — e também pra uma fase de revisão de proposta, se quiser prazo pra revisar. Só pede a data: não exige produzir um orçamento novo." />
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Switch
                        size="sm"
                        label="Marcar o orçamento como concluído nesta fase"
                        checked={Boolean(f.isOrcamentoFeito)}
                        onChange={(e) =>
                          acoes.editar.mutate(
                            { id: f.id, isOrcamentoFeito: e.target.checked },
                            { onError: () => toast.show('Não foi possível salvar a fase.', { variant: 'error' }) },
                          )
                        }
                      />
                      <HelpTip texto="Ao entrar aqui, o sistema registra que o orçamento ficou pronto (data e quem concluiu). Ligue só na fase que encerra a produção do orçamento. Fase de revisão de proposta não precisa desta opção — revisar uma versão não é produzir orçamento de novo." />
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {/* CA-1: fases desativadas — atenuadas, com Reativar. */}
        {inativas.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-bold uppercase tracking-wide text-ink-3 dark:text-ink-dark-3 mb-1.5">
              Fases desativadas
            </p>
            <div className="space-y-1.5">
              {inativas.map((f) => (
                <div
                  key={f.id}
                  className="flex flex-wrap items-center gap-x-2 gap-y-1.5 rounded-lg border border-line-soft dark:border-line-soft-dark px-3 py-2 opacity-60"
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: f.cor }} />
                  <span className="flex-1 min-w-0 break-words text-sm font-medium text-ink-1 dark:text-ink-dark-1">
                    {f.nome}
                  </span>
                  <button
                    type="button"
                    data-testid={`cfg-fase-reativar-${f.id}`}
                    className="text-xs text-ink-3 dark:text-ink-dark-3 hover:text-red"
                    onClick={() =>
                      acoes.editar.mutate(
                        { id: f.id, ativo: true },
                        {
                          onSuccess: () => toast.show('Fase reativada — ela volta pro funil.'),
                          onError: () => toast.show('Não foi possível reativar a fase.', { variant: 'error' }),
                        },
                      )
                    }
                  >
                    reativar
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-3 inline-flex items-center gap-1">
          Como o orçamento anda dentro do funil
          <HelpTip texto="Na fase que dispara o pedido de orçamento, ligue 'Pedir data de entrega' — ao mover um card pra ela, o sistema pede a data e cobra o prazo em dias úteis. Na fase que encerra a produção do orçamento, ligue 'Marcar o orçamento como concluído'. Uma fase de revisão de proposta pode pedir só a data (prazo de revisar), sem marcar conclusão — o orçamento já existe." />
        </p>
        <div className="flex flex-wrap gap-2">
          <Input
            size="sm"
            wrapperClassName="flex-1 min-w-[160px]"
            placeholder="Nova fase (entra antes de Ganho)…"
            value={novaFase}
            onChange={(e) => setNovaFase(e.target.value)}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={!novaFase.trim()}
            onClick={async () => {
              try {
                await acoes.criar.mutateAsync({ nome: novaFase.trim() });
                setNovaFase('');
                toast.show('Fase criada.');
              } catch (err) {
                toast.show(mensagemErroConfig(err, 'Já existe uma fase com esse nome neste funil.'), {
                  variant: 'error',
                });
              }
            }}
          >
            Adicionar
          </Button>
        </div>
        <Dialog open={Boolean(renomeando)} onOpenChange={(o) => !o && setRenomeando(null)} size="sm">
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Renomear fase</DialogTitle>
            </DialogHeader>
            <Input value={renomeando?.nome ?? ''} onChange={(e) => renomeando && setRenomeando({ ...renomeando, nome: e.target.value })} />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setRenomeando(null)}>
                Cancelar
              </Button>
              <Button
                onClick={async () => {
                  if (!renomeando) return;
                  try {
                    await acoes.editar.mutateAsync({ id: renomeando.id, nome: renomeando.nome });
                    setRenomeando(null);
                  } catch (err) {
                    toast.show(mensagemErroConfig(err, 'Já existe uma fase com esse nome neste funil.'), {
                      variant: 'error',
                    });
                  }
                }}
              >
                Salvar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CA-1: confirmação de desativar fase. */}
        <Dialog open={Boolean(desativando)} onOpenChange={(o) => !o && setDesativando(null)} size="sm">
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Desativar a fase “{desativando?.nome}”?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-ink-2 dark:text-ink-dark-2">
              A fase some do funil e das listas de escolha. Nada é apagado: o histórico das propostas que
              passaram por ela continua igual, e dá pra reativar a fase aqui quando quiser.
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDesativando(null)}>
                Cancelar
              </Button>
              <Button data-testid="cfg-fase-desativar-confirmar" onClick={() => void confirmarDesativar()}>
                Desativar fase
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CA-1: guia quando a fase ainda tem propostas ativas (422 fase-com-cards). */}
        <Dialog open={Boolean(bloqueio)} onOpenChange={(o) => !o && setBloqueio(null)} size="sm">
          <DialogContent data-testid="cfg-fase-bloqueio-cards">
            <DialogHeader>
              <DialogTitle>Ainda tem proposta nesta fase</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-ink-2 dark:text-ink-dark-2">
              A fase <b>{bloqueio?.fase.nome}</b> tem{' '}
              <b>
                {bloqueio?.count === 1 ? '1 proposta ativa' : `${bloqueio?.count ?? 0} propostas ativas`}
              </b>
              . Pra desativar a fase, primeiro mova essas propostas pra outra fase (no Funil, arraste os
              cards ou abra cada um e troque a fase). Depois volte aqui e desative.
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setBloqueio(null)}>
                Entendi
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>

      <Card variant="flat" className="p-4 min-w-0">
        <h3 className="text-base font-bold text-ink-1 dark:text-ink-dark-1 mb-1 inline-flex items-center gap-1.5">
          Campos obrigatórios por fase
          <HelpTip texto="Trava de qualidade: o card só entra na fase se estes campos estiverem preenchidos. Clique num campo pra ligar ou desligar a exigência." />
        </h3>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mb-3">
          O card só ENTRA na fase se estes campos estiverem preenchidos.
        </p>
        <div className="space-y-3">
          {/* CA-1: fase desativada não configura campos (some da lista). */}
          {abertasAtivas
            .map((f) => {
              const regras = (campos.data?.regras ?? []).filter((r) => r.faseId === f.id).map((r) => r.campo);
              return (
                <div key={f.id}>
                  <p className="text-sm font-bold text-ink-2 dark:text-ink-dark-2 mb-1.5">{f.nome}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(campos.data?.camposValidos ?? []).map((c) => {
                      const on = regras.includes(c);
                      return (
                        <button
                          key={c}
                          type="button"
                          onClick={() =>
                            geral.salvarCamposObrigatorios.mutate(
                              {
                                faseId: f.id,
                                campos: on ? regras.filter((x) => x !== c) : [...regras, c],
                              },
                              {
                                onError: () =>
                                  toast.show('Não foi possível salvar os campos obrigatórios.', { variant: 'error' }),
                              },
                            )
                          }
                          className={cn(
                            'text-sm font-semibold px-2.5 py-1 rounded-pill border transition-colors',
                            on
                              ? 'border-red bg-red-soft dark:bg-red-soft-dark text-red dark:text-red-glow'
                              : 'border-line dark:border-line-dark text-ink-3 dark:text-ink-dark-3',
                          )}
                        >
                          {CAMPO_LABEL[c] ?? c}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
        </div>
      </Card>
    </div>
  );
}

// ─── Aba: Automações (construtor Quando / Filtros / Então) ─────────────



/** Resumo humano do "quando / filtros / então" mostrado no card da regra. */
